import React, { useEffect, useState } from "react";
import { AppShell } from "@/components/bi/AppShell";
import { EmptyState } from "@/components/bi/EmptyState";
import { Loading } from "@/components/bi/Loading";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Users, UserPlus, Trash2, Shield, Loader2, Lock, Palette, ScrollText, KeySquare, ShieldCheck, HardDrive, Bot, Gauge, KeyRound, Eye, EyeOff, Bug, ShieldQuestion, BarChart3, RefreshCw, Database } from "lucide-react";
import { ROLE_LABELS, roleLabel, assignableRoles, isTenantAdmin, isSuperAdmin } from "@/lib/roles";
import { TenantSourcePolicyCard } from "@/components/bi/TenantSourcePolicyCard";
import { AdminAudit } from "@/components/bi/AdminAudit";
import { AdminSsoKeys } from "@/components/bi/AdminSsoKeys";
import { AdminSchemaDrift } from "@/components/bi/AdminSchemaDrift";
import { AdminAiGateway } from "@/components/bi/AdminAiGateway";
import { AdminPerf } from "@/components/bi/AdminPerf";
import { AdminDiagnostics } from "@/components/bi/AdminDiagnostics";
import { StorageSettings } from "@/components/bi/StorageSettings";
import { AdminRoles } from "@/components/bi/AdminRoles";
import { AdminPermissions } from "@/components/bi/AdminPermissions";
import { TenantAiKeyCard } from "@/components/bi/TenantAiKeyCard";
import { GoogleMapsKeyCard } from "@/components/bi/GoogleMapsKeyCard";
import { AdminUsage } from "@/components/bi/AdminUsage";
import { AdminRefreshMonitor } from "@/components/bi/AdminRefreshMonitor";

export default function Admin() {
  const { user } = useAuth();
  const { branding, tenant, saveBranding } = useBranding();
  const canManage = isTenantAdmin(user?.role);
  const superAdmin = isSuperAdmin(user?.role);

  // The tenant's *own* branding record (never merged with universal defaults) —
  // driving the form off this record means an operator can leave a field blank
  // to inherit the platform default and a subsequent save keeps that inheritance.
  const tenantOwnBrand = tenant?.branding || {};
  const [platform, setPlatform] = useState({ default_logo_url: "", default_primary_color: "", default_accent_color: "" });

  const [users, setUsers] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "viewer", tenant_id: "" });
  const [bForm, setBForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pwTarget, setPwTarget] = useState(null);         // user whose password is being reset
  const [pwValue, setPwValue] = useState("");
  const [pwShow, setPwShow] = useState(false);

  const roleChoices = assignableRoles(user?.role);

  useEffect(() => { setBForm({ ...tenantOwnBrand }); }, [tenant]);
  // Load universal defaults so we can render them as placeholders — makes it
  // obvious which fields are inherited vs. explicitly set on this tenant.
  useEffect(() => {
    import("@/hooks/usePlatformSettings").then(({ loadPlatformSettings }) =>
      loadPlatformSettings().then((p) => setPlatform({
        default_logo_url: p?.default_logo_url || "",
        default_primary_color: p?.default_primary_color || "",
        default_accent_color: p?.default_accent_color || "",
      })).catch(() => { /* ignore */ }),
    );
  }, []);

  const loadUsers = async () => {
    try { const { data } = await api.get("/auth/users"); setUsers(data); }
    catch { setUsers([]); }
  };
  const loadTenants = async () => {
    try { const { data } = await api.get("/tenants"); setTenants(data); }
    catch { setTenants([]); }
  };
  useEffect(() => { if (canManage) { loadUsers(); loadTenants(); } }, [canManage]);

  const create = async () => {
    if (!form.name || !form.email || !form.password) { toast.error("Fill all fields"); return; }
    setSaving(true);
    try {
      const payload = { name: form.name, email: form.email, password: form.password, role: form.role };
      await api.post("/auth/users", payload);
      toast.success(`User ${form.email} created`);
      setOpen(false); setForm({ name: "", email: "", password: "", role: "viewer", tenant_id: "" }); loadUsers();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to create user"); }
    finally { setSaving(false); }
  };

  const updateRole = async (u, role) => {
    try { await api.put(`/auth/users/${u.id}`, { role }); toast.success("Role updated"); loadUsers(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (u) => {
    try { await api.delete(`/auth/users/${u.id}`); toast.success("User removed"); loadUsers(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const openReset = (u) => { setPwTarget(u); setPwValue(""); setPwShow(false); };
  const submitReset = async () => {
    if (!pwTarget) return;
    if ((pwValue || "").length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setSaving(true);
    try {
      await api.put(`/auth/users/${pwTarget.id}`, { password: pwValue });
      toast.success(`Password reset for ${pwTarget.email}`);
      setPwTarget(null); setPwValue("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  const generatePw = () => {
    // 12-char, url-safe, easy to read
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
    let out = ""; for (let i = 0; i < 12; i++) out += chars[Math.floor(Math.random() * chars.length)];
    setPwValue(out); setPwShow(true);
  };

  const tenantName = (id) => tenants.find((t) => t.id === id)?.name || id || "default";

  const saveBrandingForm = async () => {
    if (!tenant?.id) { toast.error("Tenant not loaded"); return; }
    setSaving(true);
    try {
      await saveBranding(tenant.id, {
        app_name: bForm.app_name, tagline: bForm.tagline,
        primary_color: bForm.primary_color, accent_color: bForm.accent_color,
        logo_url: bForm.logo_url, theme: bForm.theme,
      });
      toast.success("Branding saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save branding"); }
    finally { setSaving(false); }
  };

  if (!canManage) {
    return <AppShell title="Admin Console"><div className="px-5 md:px-8 py-10"><EmptyState icon={Lock} title="Administrators only" description="Only tenant administrators and above can manage users and tenants." /></div></AppShell>;
  }

  return (
    <AppShell title="Admin Console">
      {/* Reset user password dialog */}
      <Dialog open={!!pwTarget} onOpenChange={(o) => !o && setPwTarget(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">Reset password</DialogTitle></DialogHeader>
          <div className="space-y-3 py-1">
            <p className="text-sm text-[color:var(--ink-3)]">Set a new password for <span className="text-[color:var(--ink)] font-medium">{pwTarget?.email}</span>. Please share it with them over a secure channel.</p>
            <div>
              <Label>New password</Label>
              <div className="relative mt-1.5">
                <Input data-testid="admin-reset-pw-input" type={pwShow ? "text" : "password"} value={pwValue} onChange={(e) => setPwValue(e.target.value)} className="bg-[color:var(--surface)] pr-10" />
                <button type="button" onClick={() => setPwShow((v) => !v)} aria-label={pwShow ? "Hide password" : "Show password"} className="absolute inset-y-0 right-0 flex items-center px-3 text-[color:var(--ink-3)] hover:text-[color:var(--ink)]">
                  {pwShow ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={generatePw} data-testid="admin-reset-pw-generate">Generate secure password</Button>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPwTarget(null)}>Cancel</Button>
            <Button data-testid="admin-reset-pw-confirm" onClick={submitReset} disabled={saving || !pwValue} className="bg-[color:var(--brand-ocean)] text-white">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update password"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create user dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">Create user login</DialogTitle></DialogHeader>
          <div className="space-y-3 py-1">
            <div><Label>Name</Label><Input data-testid="admin-user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 bg-[color:var(--surface)]" /></div>
            <div><Label>Email</Label><Input data-testid="admin-user-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 bg-[color:var(--surface)]" /></div>
            <div><Label>Temporary password</Label><Input data-testid="admin-user-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5 bg-[color:var(--surface)]" /></div>
            <div><Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="admin-user-role" className="mt-1.5 bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
                <SelectContent>{roleChoices.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {superAdmin ? (
              <div><Label>Tenant</Label>
                <Select value={form.tenant_id || "default"} onValueChange={(v) => setForm({ ...form, tenant_id: v })}>
                  <SelectTrigger data-testid="admin-user-tenant" className="mt-1.5 bg-[color:var(--surface)]"><SelectValue placeholder="Tenant" /></SelectTrigger>
                  <SelectContent>{tenants.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            ) : null}
          </div>
          <DialogFooter><Button data-testid="admin-user-create-confirm" onClick={create} disabled={saving} className="bg-[color:var(--brand-ocean)] text-white">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create user"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create tenant dialog removed — tenant management lives in /superadmin */}

      <div className="px-5 md:px-8 py-6">
        <Tabs defaultValue="users">
          <TabsList className="bg-[color:var(--surface-2)]">
            <TabsTrigger value="users" data-testid="admin-tab-users"><Users className="w-4 h-4 mr-1.5" />Users</TabsTrigger>
            <TabsTrigger value="roles" data-testid="admin-tab-roles"><ShieldQuestion className="w-4 h-4 mr-1.5" />Roles</TabsTrigger>
            <TabsTrigger value="permissions" data-testid="admin-tab-permissions"><Shield className="w-4 h-4 mr-1.5" />Permissions</TabsTrigger>
            <TabsTrigger value="sources" data-testid="admin-tab-sources"><Database className="w-4 h-4 mr-1.5" />Data Sources</TabsTrigger>
            <TabsTrigger value="branding" data-testid="admin-tab-branding"><Palette className="w-4 h-4 mr-1.5" />Branding</TabsTrigger>
            <TabsTrigger value="audit" data-testid="admin-tab-audit"><ScrollText className="w-4 h-4 mr-1.5" />Audit Logs</TabsTrigger>
            <TabsTrigger value="sso" data-testid="admin-tab-sso"><KeySquare className="w-4 h-4 mr-1.5" />SSO Keys</TabsTrigger>
            <TabsTrigger value="drift" data-testid="admin-tab-drift"><ShieldCheck className="w-4 h-4 mr-1.5" />Schema Drift</TabsTrigger>
            <TabsTrigger value="storage" data-testid="admin-tab-storage"><HardDrive className="w-4 h-4 mr-1.5" />Storage</TabsTrigger>
            <TabsTrigger value="ai-gateway" data-testid="admin-tab-ai-gateway"><Bot className="w-4 h-4 mr-1.5" />AI Gateway</TabsTrigger>
            <TabsTrigger value="performance" data-testid="admin-tab-performance"><Gauge className="w-4 h-4 mr-1.5" />Performance</TabsTrigger>
            <TabsTrigger value="usage" data-testid="admin-tab-usage"><BarChart3 className="w-4 h-4 mr-1.5" />Usage</TabsTrigger>
            <TabsTrigger value="refresh" data-testid="admin-tab-refresh"><RefreshCw className="w-4 h-4 mr-1.5" />Refresh Monitor</TabsTrigger>
            <TabsTrigger value="diagnostics" data-testid="admin-tab-diagnostics"><Bug className="w-4 h-4 mr-1.5" />Diagnostics</TabsTrigger>
          </TabsList>

          <TabsContent value="audit" className="mt-4"><AdminAudit /></TabsContent>
          <TabsContent value="roles" className="mt-4"><AdminRoles /></TabsContent>
          <TabsContent value="permissions" className="mt-4"><AdminPermissions users={users || []} /></TabsContent>
          <TabsContent value="sources" className="mt-4"><TenantSourcePolicyCard /></TabsContent>
          <TabsContent value="sso" className="mt-4"><AdminSsoKeys /></TabsContent>
          <TabsContent value="drift" className="mt-4"><AdminSchemaDrift /></TabsContent>
          <TabsContent value="storage" className="mt-4">
            <div className="space-y-4">
              <StorageSettings />
            </div>
          </TabsContent>
          <TabsContent value="ai-gateway" className="mt-4"><AdminAiGateway /></TabsContent>
          <TabsContent value="performance" className="mt-4"><AdminPerf /></TabsContent>
          <TabsContent value="usage" className="mt-4"><AdminUsage /></TabsContent>
          <TabsContent value="refresh" className="mt-4"><AdminRefreshMonitor /></TabsContent>
          <TabsContent value="diagnostics" className="mt-4"><AdminDiagnostics /></TabsContent>

          <TabsContent value="users" className="mt-4">
            <div className="flex justify-end mb-3">
              <Button data-testid="admin-add-user" onClick={() => setOpen(true)} className="bg-[color:var(--brand-ocean)] hover:bg-[#1E3AD9] text-white"><UserPlus className="w-4 h-4 mr-1" />Create user</Button>
            </div>
            {users === null ? <Loading label="Loading users" /> : users.length === 0 ? (
              <EmptyState icon={Users} title="No users yet" description="Create user logins and assign roles." />
            ) : (
              <div className="glass-card overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-[color:var(--surface-2)]"><tr>
                    <th className="text-left px-4 py-3 font-medium text-[color:var(--ink-2)]">Name</th>
                    <th className="text-left px-4 py-3 font-medium text-[color:var(--ink-2)]">Email</th>
                    <th className="text-left px-4 py-3 font-medium text-[color:var(--ink-2)]">Role</th>
                    <th className="text-right px-4 py-3 font-medium text-[color:var(--ink-2)]">Actions</th>
                  </tr></thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id} data-testid="admin-user-row" className="border-t border-[color:var(--glass-border)]">
                        <td className="px-4 py-3 text-[color:var(--ink)]">{u.name}</td>
                        <td className="px-4 py-3 text-[color:var(--ink-2)]">{u.email}</td>
                        <td className="px-4 py-3">
                          {u.id === user.id ? (
                            <Badge className="bg-[color:rgba(18,132,174,0.15)] text-[color:var(--brand-ocean)] border-0"><Shield className="w-3 h-3 mr-1" />{roleLabel(u.role)} (you)</Badge>
                          ) : roleChoices.includes(u.role) || roleChoices.length ? (
                            <Select value={u.role} onValueChange={(v) => updateRole(u, v)}>
                              <SelectTrigger data-testid={`admin-role-${u.id}`} className="w-[150px] h-8 bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
                              <SelectContent>{assignableRoles(user?.role).concat(roleChoices.includes(u.role) ? [] : [u.role]).map((r) => <SelectItem key={r} value={r}>{roleLabel(r)}</SelectItem>)}</SelectContent>
                            </Select>
                          ) : (
                            <Badge className="bg-[color:var(--surface-2)] text-[color:var(--ink-2)] border-0">{roleLabel(u.role)}</Badge>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {u.id !== user.id && (
                            <>
                              <Button size="sm" variant="ghost" onClick={() => openReset(u)} data-testid={`admin-reset-pw-${u.id}`} title="Reset password" className="text-[color:var(--ink-2)] hover:text-[color:var(--brand-ocean)]"><KeyRound className="w-4 h-4" /></Button>
                              <Button size="sm" variant="ghost" onClick={() => del(u)} className="text-[color:var(--danger)]"><Trash2 className="w-4 h-4" /></Button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="branding" className="mt-4">
            {bForm && (
              <div className="grid md:grid-cols-2 gap-5 max-w-4xl">
                <div className="glass-card p-5 space-y-4">
                  <h3 className="font-display text-lg text-[color:var(--ink)]">Tenant branding</h3>
                  <div><Label>App name</Label><Input data-testid="brand-name-input" value={bForm.app_name || ""} onChange={(e) => setBForm({ ...bForm, app_name: e.target.value })} className="mt-1.5 bg-[color:var(--surface)]" /></div>
                  <div><Label>Tagline</Label><Input data-testid="brand-tagline-input" value={bForm.tagline || ""} onChange={(e) => setBForm({ ...bForm, tagline: e.target.value })} className="mt-1.5 bg-[color:var(--surface)]" /></div>
                  <div><Label>Logo URL</Label>
                    <Input data-testid="brand-logo-input"
                           value={bForm.logo_url || ""}
                           onChange={(e) => setBForm({ ...bForm, logo_url: e.target.value })}
                           placeholder={platform.default_logo_url || "https://...png"}
                           className="mt-1.5 bg-[color:var(--surface)]" />
                    {!bForm.logo_url && platform.default_logo_url && (
                      <p className="text-[10px] text-[color:var(--ink-3)] mt-1">Inheriting platform default</p>
                    )}
                  </div>
                  <div className="flex gap-4">
                    <div className="flex-1">
                      <Label>Primary color</Label>
                      <div className="flex items-center gap-2 mt-1.5">
                        <input data-testid="brand-primary-color" type="color"
                               value={bForm.primary_color || platform.default_primary_color || "#2B4EFF"}
                               onChange={(e) => setBForm({ ...bForm, primary_color: e.target.value })}
                               className="h-9 w-12 rounded-lg border border-[color:var(--glass-border)] bg-transparent cursor-pointer" />
                        <Input data-testid="brand-primary-color-hex"
                               value={bForm.primary_color || ""}
                               onChange={(e) => setBForm({ ...bForm, primary_color: e.target.value })}
                               placeholder={platform.default_primary_color || "#2B4EFF"}
                               className="bg-[color:var(--surface)] font-mono" />
                      </div>
                      {!bForm.primary_color && platform.default_primary_color && (
                        <p className="text-[10px] text-[color:var(--ink-3)] mt-1">Inheriting <span className="font-mono">{platform.default_primary_color}</span></p>
                      )}
                    </div>
                    <div className="flex-1">
                      <Label>Accent color</Label>
                      <div className="flex items-center gap-2 mt-1.5">
                        <input data-testid="brand-accent-color" type="color"
                               value={bForm.accent_color || platform.default_accent_color || "#FF6B35"}
                               onChange={(e) => setBForm({ ...bForm, accent_color: e.target.value })}
                               className="h-9 w-12 rounded-lg border border-[color:var(--glass-border)] bg-transparent cursor-pointer" />
                        <Input data-testid="brand-accent-color-hex"
                               value={bForm.accent_color || ""}
                               onChange={(e) => setBForm({ ...bForm, accent_color: e.target.value })}
                               placeholder={platform.default_accent_color || "#FF6B35"}
                               className="bg-[color:var(--surface)] font-mono" />
                      </div>
                      {!bForm.accent_color && platform.default_accent_color && (
                        <p className="text-[10px] text-[color:var(--ink-3)] mt-1">Inheriting <span className="font-mono">{platform.default_accent_color}</span></p>
                      )}
                    </div>
                  </div>
                  <div><Label>Default theme</Label>
                    <Select value={bForm.theme || "light"} onValueChange={(v) => setBForm({ ...bForm, theme: v })}>
                      <SelectTrigger data-testid="brand-theme-select" className="mt-1.5 bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="light">Light</SelectItem><SelectItem value="dark">Dark</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <Button data-testid="brand-save" onClick={saveBrandingForm} disabled={saving} className="w-full bg-[color:var(--brand-ocean)] text-white">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save branding"}</Button>
                </div>

                <div className="glass-card p-5">
                  <h3 className="font-display text-lg text-[color:var(--ink)] mb-3">Live preview</h3>
                  <div className="rounded-2xl border border-[color:var(--glass-border)] overflow-hidden">
                    <div className="flex items-center gap-2.5 p-4" style={{ background: "var(--surface-2)" }}>
                      {bForm.logo_url ? <img src={bForm.logo_url} alt="logo" className="w-9 h-9 rounded-xl object-cover" /> : <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: bForm.primary_color }}><Palette className="w-5 h-5 text-white" /></div>}
                      <div><div className="font-display font-semibold text-[color:var(--ink)]">{bForm.app_name || "App name"}</div><div className="text-[11px] text-[color:var(--ink-3)]">{bForm.tagline}</div></div>
                    </div>
                    <div className="p-4 space-y-3" style={{ background: "var(--surface)" }}>
                      <div className="h-2.5 rounded-full" style={{ width: "70%", background: bForm.primary_color }} />
                      <div className="h-2.5 rounded-full" style={{ width: "45%", background: bForm.accent_color }} />
                      <div className="flex gap-2 pt-1">
                        <span className="px-3 py-1.5 rounded-lg text-white text-xs font-medium" style={{ background: bForm.primary_color }}>Primary</span>
                        <span className="px-3 py-1.5 rounded-lg text-white text-xs font-medium" style={{ background: bForm.accent_color }}>Accent</span>
                      </div>
                    </div>
                  </div>
                  <p className="text-[11px] text-[color:var(--ink-3)] mt-3">Use the header sun/moon toggle to preview dark mode. The default theme above applies for everyone in this tenant.</p>
                </div>

                <TenantAiKeyCard tenantId={tenant?.id} />
                <GoogleMapsKeyCard tenantId={tenant?.id} />
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </AppShell>
  );
}

